/**
 * Ticket sur imprimante Sunmi intégrée, mise en page d'origine : tous les terminaux sauf le D3.
 * Le D3 Mini et les terminaux V2 gardent cette sortie, la nouvelle mise en page n'a été
 * validée que sur le D3 80 mm (voir `aidl-receipt-layout.ts`).
 */
import { devLog } from '@/lib/logger';
import { wrapText, paymentRows } from './escpos-formatter';
import type { TicketData } from './escpos-types';
import { globalDiscountRows, formatTicketAmount } from './ticket-discount';
import { ticketFiscalMention } from '@/config/editor';
import { logPrintAttempt } from '@/api/printing/logging';

export async function sendViaAidlPreviousLayout(data: TicketData): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL Sunmi non typé (objet Android exposé via WebView)
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (!b) return false;

  const CONNECT_TIMEOUT_MS = 8_000;
  const MAX_RETRIES = 3;
  const RETRY_BACKOFF_MS = 400;

  // Attente de connexion (borné à CONNECT_TIMEOUT_MS total)
  const connectStart = Date.now();
  for (let i = 0; i < 5; i++) {
    if (Date.now() - connectStart >= CONNECT_TIMEOUT_MS) break;
    try {
      if (b.isConnected?.()) break;
    } catch {
      /* ignore */
    }
    try {
      b.init?.();
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, 600 + i * 200));
  }

  // Taille légèrement plus petite sur Sunmi AIDL — le firmware natif rend
  // plus gras qu'ESC/POS à même pixel size.
  const ps = Math.max(14, Math.min(22, data.config.police_size ? data.config.police_size - 2 : 18));
  const psHeader = ps + 3;
  const psTotal = ps + 3;
  const cpl = data.config.largeur_papier === 58 ? 32 : 48;

  const hasPrintColumns = typeof b.printColumns === 'function';

  /**
   * Émet une ligne avec alignement via printColumns (atomique) si disponible,
   * sinon via setAlignment + printText (ordre IPC non garanti).
   */
  const printLine = (text: string, align: 0 | 1 | 2 = 0) => {
    if (hasPrintColumns) {
      try {
        b.printColumns(JSON.stringify([{ text, width: cpl, align }]));
        return;
      } catch {
        /* fall through */
      }
    }
    b.setAlignment(align);
    b.printText(text);
  };

  /**
   * Ligne à deux colonnes (article + prix, sous-total…).
   * `shrink` réduit le budget total de caractères — nécessaire pour les polices
   * bold+large (psTotal/psHeader) où la largeur firmware est plus étroite.
   */
  const printRow = (left: string, right: string, shrink = 1) => {
    const totalW = Math.max(12, Math.floor(cpl * shrink));
    const nameW = Math.floor(totalW * 0.65);
    const priceW = totalW - nameW;
    if (hasPrintColumns) {
      try {
        b.printColumns(
          JSON.stringify([
            { text: left, width: nameW, align: 0 },
            { text: right, width: priceW, align: 2 },
          ]),
        );
        return;
      } catch {
        /* fall through to manual padding */
      }
    }
    const maxLeft = totalW - right.length - 1;
    const trimmedLeft = left.length > maxLeft ? left.substring(0, maxLeft) : left;
    const space = Math.max(1, totalW - trimmedLeft.length - right.length);
    b.printText(trimmedLeft + ' '.repeat(space) + right);
  };

  const sep = '-'.repeat(cpl);
  const dsep = '='.repeat(cpl);

  let lastAidlErr: unknown;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const printStart = Date.now();
    try {
      b.beginTransaction();

      // Bandeau DUPLICATA (NF525 art. 286 — copie identifiée comme telle)
      if (data.isDuplicate) {
        b.setBold(true);
        b.setFontSize(psHeader + 4);
        printLine('*** DUPLICATA ***', 1);
        b.setFontSize(ps);
        b.setBold(false);
        b.printText('\n');
      }

      // Header — tout centré via printColumns natif (pas de dépendance setAlignment)
      b.setBold(true);
      b.setFontSize(psHeader);
      printLine(data.store_name, 1);
      b.setBold(false);
      b.setFontSize(ps);
      if (data.store_subtitle) printLine(data.store_subtitle, 1);
      if (data.store_address) data.store_address.split('\n').forEach((l) => printLine(l, 1));
      if (data.store_phone) printLine('Tel : ' + data.store_phone, 1);
      if (data.store_email) printLine(data.store_email, 1);
      if (data.store_siret) printLine('SIRET : ' + data.store_siret, 1);
      if (data.tva_intracom) printLine('TVA Intra : ' + data.tva_intracom, 1);

      // Meta — centré
      b.setFontSize(ps);
      b.printText(sep);
      if (data.config.show_num_ticket && data.ticket_number) {
        b.setBold(true);
        b.setFontSize(ps + 2);
        printLine(data.ticket_number, 1);
        b.setBold(false);
      }
      b.setFontSize(ps);
      if (data.config.show_date && data.config.show_heure)
        printLine(data.date + ' ' + data.time, 1);
      else if (data.config.show_date) printLine(data.date, 1);
      else if (data.config.show_heure) printLine(data.time, 1);
      if (data.config.show_vendeur) printLine('Vendeur : ' + data.cashier, 1);
      printLine(data.register, 1);
      if (data.config.show_client && data.client) printLine('Client : ' + data.client, 1);

      // Items
      b.setFontSize(ps + 2);
      b.printText(sep);
      for (const item of data.items) {
        if (data.config.show_ref_article && item.ref) {
          b.setFontSize(ps - 2);
          printLine('  ' + item.ref, 0);
          b.setFontSize(ps + 2);
        }
        printRow(
          item.quantity + 'x ' + item.name,
          (item.quantity * item.unit_price).toFixed(2) + ' EUR',
          0.9,
        );
      }
      b.printText(sep);
      for (const row of globalDiscountRows(data))
        printRow(row.label, formatTicketAmount(row.amount), 0.9);

      // TVA
      if (data.config.show_detail_tva) {
        b.setFontSize(ps);
        printRow('Sous-total HT', data.total_ht.toFixed(2) + ' EUR', 0.9);
        if (data.config.show_tva_par_taux) {
          for (const tva of data.tva_breakdown) {
            printRow('TVA ' + tva.rate + '%', tva.tva.toFixed(2) + ' EUR', 0.9);
          }
        }
      }

      // Total — shrink à 80% pour éviter le retour à la ligne firmware ("TOTAL TTC\n10.20 EU\nR")
      b.printText(dsep);
      b.setBold(true);
      b.setFontSize(psTotal);
      printRow('TOTAL TTC', data.total_ttc.toFixed(2) + ' EUR', 0.8);
      b.setBold(false);
      b.setFontSize(ps);
      b.printText(dsep);

      // Payment — une ligne par moyen (fractionné) ou la ligne Reglement classique
      if (data.config.show_mode_reglement) {
        b.setFontSize(ps + 2);
        for (const row of paymentRows(data)) printRow(row.label, row.value, 0.9);
        if (data.card_line) {
          // Réglage retour_tpe_detaille : une ligne « CB : VISA ****1234 · Aut. … · TID … »
          b.setFontSize(ps - 2);
          for (const l of wrapText(data.card_line, cpl - 2)) printLine('  ' + l, 0);
          b.setFontSize(ps);
        } else if (data.card_scheme) {
          b.setFontSize(ps - 2);
          // TPE bancaire externe : pas d'EMV myPOS, mention + ref optionnelle
          if (data.card_scheme === 'EXTERNAL_TPE') {
            const refSuffix = data.rrn ? ' - ref ' + data.rrn : '';
            printLine('  TPE externe' + refSuffix, 0);
          } else {
            printLine('  ' + data.card_scheme, 0);
            if (data.pan_masked) printLine('  PAN: ' + data.pan_masked, 0);
            if (data.auth_code) printLine('  Auth: ' + data.auth_code, 0);
            if (data.aid) printLine('  AID: ' + data.aid, 0);
            if (data.entry_mode) printLine('  Mode: ' + data.entry_mode, 0);
            if (data.tid) printLine('  TID: ' + data.tid, 0);
            if (data.mid) printLine('  MID: ' + data.mid, 0);
          }
          b.setFontSize(ps);
        }
      }

      // Footer — word-wrap pour que les mentions légales longues cassent sur les espaces
      b.setFontSize(ps);
      if (data.config.footer_legal)
        for (const l of wrapText(data.config.footer_legal, cpl)) printLine(l, 1);
      if (data.config.footer_text)
        for (const l of wrapText(data.config.footer_text, cpl)) printLine(l, 1);
      if (data.config.show_merci) {
        b.setBold(true);
        b.setFontSize(ps + 1);
        for (const l of wrapText(data.config.merci_text || 'Merci de votre visite !', cpl))
          printLine(l, 1);
        b.setBold(false);
      }
      if (data.config.mention_agec) {
        b.setFontSize(ps - 2);
        for (const l of wrapText(data.config.mention_agec, cpl)) printLine(l, 1);
      }
      b.setFontSize(ps - 2);
      for (const l of wrapText(ticketFiscalMention(data.config.mention_nf525), cpl))
        printLine(l, 1);

      // QR Code
      if (data.config.show_qr_code && typeof b.printQRCode === 'function') {
        b.setAlignment(1);
        const qrContent = data.config.qr_code_content || data.store_name;
        try {
          b.printQRCode({ data: qrContent, moduleSize: 6, errorLevel: 1 });
        } catch {
          /* ignore — certains firmwares ne supportent pas printQRCode */
        }
      }

      // Sceau de sécurité NF525
      if (data.config.show_hash_fiscal && data.fiscal_hash) {
        b.printText(sep);
        b.setBold(true);
        b.setFontSize(ps);
        printLine('SCEAU DE SECURITE', 1);
        b.setBold(false);
        b.setFontSize(ps);
        const h = data.fiscal_hash;
        for (let i = 0; i < h.length; i += cpl) {
          printLine(h.substring(i, Math.min(i + cpl, h.length)), 1);
        }
        if (data.ticket_number) printLine('Ticket: ' + data.ticket_number, 1);
      }

      // Distance tête→cutter Sunmi : 2 lignes coupaient dans le bas du ticket
      // (mentions légales / hash tronqués). 4 lignes = le dernier texte passe
      // le cutter avant la coupe. Marge sûre (1 ligne blanche > contenu coupé).
      b.feedLines(4);
      b.commitTransaction();
      const durationMs = Date.now() - printStart;
      devLog('[PrintService] AIDL ticket OK attempt', attempt, durationMs + 'ms');
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: true,
        durationMs,
        metadata: { attempt },
      }).catch(() => {
        /* fire-and-forget */
      });
      return true;
    } catch (err) {
      lastAidlErr = err;
      const durationMs = Date.now() - printStart;
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[PrintService] AIDL error attempt ${attempt}/${MAX_RETRIES}:`, err);
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: false,
        durationMs,
        errorMessage: errMsg,
        metadata: { attempt },
      }).catch(() => {
        /* fire-and-forget */
      });
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS * attempt));
      }
    }
  } // end retry loop
  console.error('[PrintService] AIDL: toutes les tentatives ont échoué', lastAidlErr);
  return false;
}
